/**
 * Registration desk: payment method as a two-way toggle (Efectivo | Transferencia / QR).
 * A QR payment is a bank transfer, so both share one option; choosing it shows the event's
 * payment QR (and instructions) so the person can pay from their phone right at the desk.
 */

import { AlertTriangle } from 'lucide-react';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { PAYMENT_METHOD_LABELS, type EventPaymentMethod } from '@/Services/eventPayments.service';

export interface DeskPaymentInfo {
  qrUrl: string | null;
  instructions: string | null;
}

interface PaymentMethodToggleProps {
  value: EventPaymentMethod;
  onChange: (method: EventPaymentMethod) => void;
  paymentInfo: DeskPaymentInfo;
  disabled?: boolean;
}

export function PaymentMethodToggle({ value, onChange, paymentInfo, disabled }: PaymentMethodToggleProps) {
  const transfer = value === 'bank_transfer';

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className="text-xs">Método de pago</p>
        <SegmentedControl
          className={`w-full sm:w-auto ${disabled ? 'pointer-events-none opacity-60' : ''}`}
          value={value}
          onChange={onChange}
          options={[
            { value: 'cash' as EventPaymentMethod, label: PAYMENT_METHOD_LABELS.cash },
            { value: 'bank_transfer' as EventPaymentMethod, label: PAYMENT_METHOD_LABELS.bank_transfer },
          ]}
        />
      </div>

      {transfer &&
        (paymentInfo.qrUrl ? (
          <div className="flex flex-col items-center gap-2 rounded-md border bg-muted/30 p-3 text-center">
            <img
              src={paymentInfo.qrUrl}
              alt="QR para pagar la inscripción"
              className="size-56 max-w-full rounded bg-white object-contain p-2"
            />
            <p className="text-xs text-muted-foreground">Pida a la persona que escanee el QR y confirme el comprobante antes de cobrar.</p>
            {paymentInfo.instructions && (
              <p className="whitespace-pre-wrap text-sm">{paymentInfo.instructions}</p>
            )}
          </div>
        ) : (
          <p className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <span>
              Este evento no tiene QR de pago. Cárguelo en Eventos → Editar → Inscripción pública.
              {paymentInfo.instructions && (
                <span className="mt-1 block whitespace-pre-wrap text-foreground">{paymentInfo.instructions}</span>
              )}
            </span>
          </p>
        ))}
    </div>
  );
}
